import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useTicketCreateMutation } from "../../../../services/ticketEasyService/ticketEasy-api";
import { TicketEasySelectModule } from "./TicketEasySelectModule";
import { DefaultTicket } from "../../../../services/ticketEasyService/model/ticketEasy/Ticket";
import { useMyProfileQuery } from "../../../../services/profileService/profile-api";
import { useAppSelector } from "../../../../app/hooks";
import { EntraUser } from "../../../../services/profileService/model/entraUser/EntraUser";
import { TicketFormData, TicketSummary, steps } from "./types";
import { validateTicketForm } from "./validationUtils";
import { DialogActionButtons } from "./components/DialogActionButtons";
import { StepManager } from "./components/StepManager";
import { TicketStartCommand } from "../../../../services/ticketEasyService/commands/TicketStartCommand";
import "../../../ticketEasy/TicketEasy.css";

const TicketCreationWizard: React.FC = () => {
  const [open, setOpen] = useState(false);

  const form = useForm<TicketFormData>();
  const { handleSubmit, setValue, trigger, watch } = form;
  const [createTicket, { isLoading }] = useTicketCreateMutation();
  const { data: myProfile } = useMyProfileQuery();

  const selectedModule = useAppSelector(
    (state) => state.ticketEasyModule.TicketEasySelectedStartModule
  );

  const [createdTicketId, setCreatedTicketId] = useState<string>("");
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [ticketSummary, setTicketSummary] = useState<TicketSummary>({
    title: "",
    id: "",
    status: "",
  });

  const handleNext = async () => {
    // Before advancing to the next step, validate the current step
    const currentData = form.getValues();
    const validation = validateTicketForm(
      currentData,
      selectedModule,
      activeStep
    );

    if (!validation.isValid) {
      setValidationError(validation.errorMessage);
      // Trigger form validation to show all error messages
      await trigger();
      return;
    }

    // If we're on step 1 (Ticket Details), create the ticket
    if (activeStep === 1) {
      await handleCreateTicket();
      return;
    }

    setValidationError(null);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  const resetForm = () => {
    setValue("title", "");
    setValue("description", "");
    setValue("dueDate", "");
    setValue("attachments", []);
    setValue("ticketTypes", []);

    if (selectedModule === "UAR") {
      setValue("UARFields", {
        selectedUser: new EntraUser(),
        selectedRole: "",
        locations: [],
      });      setValue("CPSIFields", {
        newUser: new EntraUser(),
        userToCopy: null,
        locations: [],
      });
    }
  };

  const handleCreateTicket = async () => {
    const data = form.getValues();

    // Validate form data for the current step
    const validation = validateTicketForm(data, selectedModule, activeStep);
    if (!validation.isValid) {
      setValidationError(validation.errorMessage);
      // Trigger form validation to show all error messages
      await trigger();
      return;
    }

    setValidationError(null);
    const newTicket = DefaultTicket();
    newTicket.module = selectedModule;
    newTicket.title = data.title || `${selectedModule} Ticket`;
    newTicket.createdBy = myProfile?.upn || "";
    newTicket.description = data.description || "";
    newTicket.createdBy = myProfile?.identity || "";
    newTicket.dueDate = data.dueDate;
    newTicket.attachments = [];

    // Set the ticket type based on form selection
    if (data.ticketTypes && data.ticketTypes.length > 0) {
      newTicket.ticketTypes = data.ticketTypes;
    }
    if (selectedModule === "UAR") {
      if (!newTicket.ticketTypes || newTicket.ticketTypes.length === 0) {
        newTicket.ticketTypes = ["HST"];
      }

      if (newTicket.ticketTypes.includes("HST")) {
        newTicket.UARFields.user =
          data.UARFields?.selectedUser || new EntraUser();
        newTicket.UARFields.locations = data.UARFields?.locations || [];

        const locationsList =
          data.UARFields?.locations && data.UARFields.locations.length > 0
            ? `\n- Locations: ${data.UARFields.locations.join(", ")}`
            : "";

        newTicket.description = `HST Access Request\n\nUser: ${newTicket.UARFields.user.displayName}${locationsList}`;
        newTicket.title = `UAR - HST Access: ${newTicket.UARFields.user.displayName}`;
      } else if (newTicket.ticketTypes.includes("CPSI")) {
        const cpsiFields = data.CPSIFields;
        // If using a manual entry for the userToCopy, it will be passed directly as an EntraUser object
        // instead of coming from the UARFields.selectedUser
        newTicket.UARFields.user = cpsiFields?.newUser || new EntraUser();
        newTicket.UARFields.locations = cpsiFields?.locations || [];

        const locationsList =
          cpsiFields?.locations && cpsiFields.locations.length > 0
            ? `\n- Locations: ${cpsiFields.locations.join(", ")}`
            : "";

        const userToCopyName = cpsiFields?.userToCopy?.displayName || "Unknown";
        const newUserName = newTicket.UARFields.user.displayName || "New User";
        newTicket.description = `CPSI Access Request\n\n- Copy Permissions From: ${userToCopyName}${locationsList}\n- For New User: ${newUserName}`;
        newTicket.title = `UAR - CPSI Access: ${newUserName}`;
      }

      newTicket.status = "New";
      newTicket.priority = "Normal";
    }

    const command = new TicketStartCommand(newTicket);    try {
      const result = await createTicket(command).unwrap();
      setCreatedTicketId(result.id);
      setTicketSummary({
        title: newTicket.title,
        id: result.id,
        status: "Created",
      });
      // Don't reset the form when advancing to the attachments step
      // so that the data is still available when going back
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } catch (error) {
      alert("Failed to create ticket");
    }
  };

  const onCreateTicket = async (data: TicketFormData) => {
    // This will only be called when the form is submitted on step 0 (Ticket Types)
    // Validate form data for the current step
    const validation = validateTicketForm(data, selectedModule, activeStep);
    if (!validation.isValid) {
      setValidationError(validation.errorMessage);
      // Trigger form validation to show all error messages
      await trigger();
      return;
    }

    setValidationError(null);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const uploadAttachments = async () => {
    // This function is called from the button in step 1 (Ticket Details)
    await handleCreateTicket();
  };

  const handleCloseDialog = () => {
    setActiveStep(0);
    setCreatedTicketId("");
    setTicketSummary({
      title: "",
      id: "",
      status: "",
    });
    resetForm();
    setOpen(false);
  };

  const handleCloseValidationAlert = () => {
    setValidationError(null);
  };

  return (
    <React.Fragment>
      <Snackbar
        open={!!validationError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={6000}
        onClose={handleCloseValidationAlert}
      >
        <Alert
          onClose={handleCloseValidationAlert}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {validationError}
        </Alert>
      </Snackbar>
      <Stack
        direction="row"
        margin={"20px"}
        justifyContent="center"
        className="ticketEasy-module"
      >
        <TicketEasySelectModule onClick={() => setOpen(true)} />
      </Stack>
      <Dialog
        className="ticketEasy-module"
        open={open}
        onClose={() =>
          !isLoading && !isUploading && activeStep !== 1 && handleCloseDialog()
        }
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: "#f8f9fa",
            borderBottom: "1px solid #e9ecef",
            p: 3,
          }}
        >
          <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600, color: "#2c3e50" }}>
            {selectedModule === "UAR"
              ? "Create User Access Request"
              : "Create Ticket"}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} sx={{ pt: 1, pb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <form id="ticket-form" onSubmit={handleSubmit(onCreateTicket)}>
            <StepManager
              activeStep={activeStep}
              selectedModule={selectedModule}
              form={form}
              ticketSummary={ticketSummary}
              createdTicketId={createdTicketId}
            />
          </form>
        </DialogContent>

        <DialogActions sx={{ pt: 1, pb: 3, px: 3 }}>
          <DialogActionButtons
            activeStep={activeStep}
            steps={steps}
            isLoading={isLoading}
            isUploading={isUploading}
            onBack={handleBack}
            onClose={handleCloseDialog}
            onUploadAttachments={uploadAttachments}
            onNext={() => setActiveStep((prevActiveStep) => prevActiveStep + 1)}
          />
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export { TicketCreationWizard };
